import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { Trophy, Crown, Gift, Sparkles } from "lucide-react";

export default function Giveaway() {
  const [info, setInfo] = useState(null);
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    api.get("/raffle/current").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/raffle/winners").then((r) => setWinners(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="giveaway-page">
      {/* HERO */}
      <section className="relative py-16 border-b border-white/5 splatter-blue grain">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10 relative">
          <div className="label text-pfc-gold mb-3">PFC · Giveaway</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-white uppercase leading-[0.9]">
            The <span className="gold-text">Giveaway.</span>
          </h1>
          <p className="text-white/65 mt-5 font-body text-lg max-w-2xl">
            Every month one PFC member is pulled out of the raffle and crowned. Get the front cover, the highest promo,
            and merch shipped — all from playing in the casino.
          </p>
        </div>
      </section>

      {/* PRIZE FLYER */}
      <section className="py-12" data-testid="giveaway-flyer">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <PrizeFlyer />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-white/5 bg-ink-900 py-12" data-testid="giveaway-how">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="label text-pfc-gold mb-2">How it Works</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-8">
            From <span className="gold-text">Tickets</span> to <span className="silver-text">Crown</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { n: "1", t: "Earn Credits", b: "Spin slots, scratch cards, answer trivia — earn PFC Credits in the Casino." },
              { n: "2", t: "Buy Raffle Tickets", b: "100 credits per ticket. More tickets = better odds. No limit on how many you buy." },
              { n: "3", t: "Monthly Draw", b: "On the 1st of every month, one ticket is pulled at random from the pool." },
              { n: "4", t: "Win Big", b: "500 credits + front cover + highest promo + PFC hat & shirt auto-shipped." },
            ].map((s) => (
              <div key={s.n} className="pfc-card p-5">
                <div className="font-display text-5xl gold-text mb-3">{s.n}</div>
                <h3 className="font-display text-lg text-white uppercase">{s.t}</h3>
                <p className="text-white/55 font-body text-sm mt-2">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRENT POOL */}
      {info && (
        <section className="border-t border-white/5 py-12" data-testid="giveaway-current-pool">
          <div className="max-w-[900px] mx-auto px-5 lg:px-10 text-center">
            <div className="label text-pfc-gold mb-2">This Month · {info.month}</div>
            <h2 className="font-display text-3xl text-white uppercase">
              {info.total_tickets_sold} <span className="gold-text">Tickets</span> in the Pool
            </h2>
            <p className="text-white/55 font-body text-sm mt-2">Draw: {info.draw_date}</p>
            <Link
              to="/casino"
              className="inline-block mt-6 font-sub uppercase tracking-[0.25em] text-xs px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition"
              data-testid="giveaway-buy-cta"
            >
              Buy Raffle Tickets in Casino →
            </Link>
          </div>
        </section>
      )}

      {/* PAST WINNERS */}
      <section className="border-t border-white/5 bg-ink-900 py-12" data-testid="giveaway-past-winners">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="label text-pfc-gold mb-2">Past Winners</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-8">
            Hall of <span className="gold-text">Champions</span>
          </h2>
          {winners.length === 0 ? (
            <div className="text-white/40 font-sub uppercase tracking-[0.2em] text-center py-12" data-testid="giveaway-no-winners">
              First winner drawn next month · be one of them.
            </div>
          ) : (
            <ul className="space-y-3">
              {winners.map((w) => (
                <li key={w.winner_id} className="pfc-card p-5 flex items-center gap-4 border-pfc-gold/40" data-testid={`giveaway-winner-${w.month}`}>
                  <Crown className="text-pfc-gold" size={26} />
                  <div className="flex-1">
                    <p className="font-display text-2xl text-white uppercase">{w.user_name}</p>
                    <p className="text-white/55 font-body text-sm">
                      {w.month} · Pulled from {w.tickets_in_pool} tickets · +{w.credits_awarded} credits · Front cover + highest promo
                    </p>
                  </div>
                  <span className="font-sub uppercase tracking-[0.25em] text-[10px] bg-pfc-gold/15 text-pfc-gold px-3 py-1.5">
                    {new Date(w.drawn_at).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* FAIR PLAY */}
      <section className="border-t border-white/5 py-10 text-center">
        <div className="max-w-[900px] mx-auto px-5 lg:px-10">
          <p className="text-white/55 font-body text-sm">
            Raffle entries are based on chance. Winning is never guaranteed. Each ticket has equal odds.
            Participation requires a PFC account and is subject to platform terms.
          </p>
        </div>
      </section>
    </div>
  );
}


export function PrizeFlyer() {
  return (
    <div
      className="relative overflow-hidden border border-pfc-gold/50 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 p-8 sm:p-12 grain"
      data-testid="prize-flyer"
    >
      {/* Background gold splatter */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
        backgroundImage: "radial-gradient(ellipse 600px 200px at 20% 30%, #C9A24B 0%, transparent 70%), radial-gradient(ellipse 500px 200px at 80% 80%, #C9A24B 0%, transparent 60%)",
      }} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="text-pfc-gold animate-pulse" size={16} />
          <span className="label text-pfc-gold">PFC Monthly Giveaway · Official Flyer</span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.92]">
          One Winner.<br />
          <span className="gold-text">Front Cover.</span><br />
          <span className="silver-text">Every Month.</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="prize-flyer-prizes">
          {[
            { icon: <Gift size={22} />, label: "+500 Credits", sub: "Casino balance boost" },
            { icon: <Crown size={22} />, label: "Front Cover", sub: "PFC magazine cover for the month" },
            { icon: <Sparkles size={22} />, label: "Top Promo", sub: "Highest promo slot for the month" },
            { icon: <Trophy size={22} />, label: "Hat + Shirt", sub: "Auto-shipped PFC merch" },
          ].map((p) => (
            <div key={p.label} className="bg-ink-900/60 border border-pfc-gold/30 p-4 backdrop-blur">
              <div className="text-pfc-gold mb-2">{p.icon}</div>
              <p className="font-display text-lg text-white uppercase leading-tight">{p.label}</p>
              <p className="text-white/55 font-body text-xs mt-1">{p.sub}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-4 flex-wrap">
          <Link
            to="/casino"
            className="font-sub uppercase tracking-[0.25em] text-xs px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition"
            data-testid="prize-flyer-cta"
          >
            Buy Tickets in Casino →
          </Link>
          <span className="font-sub uppercase tracking-[0.2em] text-[10px] text-white/45">
            100 credits per ticket · 1 winner monthly · No purchase necessary with daily bonus credits
          </span>
        </div>
      </div>
    </div>
  );
}
